import React, { useState, useEffect } from 'react';
import styles from './PropertyMessage.scss';
import useUser from '../stores/useUser';
import { createToast } from './MaterialUi/Toast';
import LogError from '../utils/LogError';
import Button from './MaterialUi/Button';
import TextField from "./MaterialUi/TextField";
import Select from "./MaterialUi/Select";
import NumberField from "./MaterialUi/NumberField";
import { Alert, MenuItem } from "@mui/material";
import JsonApiRequest, { IUser, PatchJsonObj } from '../utils/JsonApiRequest';
import { housingSituationList, maritalStatusList, nationalityList, typeOfEmploymentList } from "./RegisterInvitedUser/UserDetailsOptions";

export interface IPropertyMessageDetailsProps {
    submitPropertyRequest: () => void;
} 

export interface IHouseholdsituationData {
  date_of_birth: string;
  place_of_birth: string;
  marital_status: keyof typeof maritalStatusList;
  type_of_employment: keyof typeof typeOfEmploymentList;
  nationality: keyof typeof nationalityList;
  housing_situation: keyof typeof housingSituationList;
  net_income: string;
}

type IEditHouseholdsituationData = Pick<IUser, 'date_of_birth' | 'place_of_birth' | 'nationality' | 'marital_status' | 'housing_situation' | 'type_of_employment' | 'net_income'>;

const PropertyMessageDetails: React.FC<React.PropsWithChildren<IPropertyMessageDetailsProps>> = ({submitPropertyRequest}) => {

    const [user] = useUser()
    const [allUserData, setAllUserData] = useState<IUser>();
    const [dateOfBirth, setDateOfBirth] = useState<string>('');
    const [currData, setCurrData] = useState<Partial<IHouseholdsituationData>>();
    const [loadingButton, setLoadingButton] = useState<null | 'skip' | 'save'>(null);

    useEffect(() => {
        if (user.isLoggedIn) {
            JsonApiRequest.getRequest(`/user/user/${user.id}`).then((res) => {
                const resData = JsonApiRequest.data2Object<IUser>(res.getData())?.[0];
                if (resData) {
                    console.log("in useEffect", resData)
                    setCurrData({
                        date_of_birth: resData.date_of_birth,
                        place_of_birth: resData.place_of_birth,
                        marital_status: resData.marital_status,
                        nationality: resData.nationality,
                        housing_situation: resData.housing_situation,
                        type_of_employment: resData.type_of_employment,
                        net_income: resData.net_income,
                    });
                    setDateOfBirth(resData.date_of_birth);
                    setAllUserData(resData)
                }
            }).catch((e) => {
                console.log(e);
                createToast('Fehler beim Laden der Kund:innen', 'error');
                LogError.add(e, {
                    title: 'Fehler beim Laden der Kund:innen',
                    error_code: 'SearchCockpitCustomers:useEffect:getCustomers',
                    request_data: {
                        user,
                    },
                });
            })
        }
    }, [user]);

    const handleDateChange = (event) => {
        const dateOfBirth = event.target.value;
        setDateOfBirth(dateOfBirth)
    }

    const handleOnBlurDate = () => {
        setCurrData({ ...currData, date_of_birth: dateOfBirth })
    }

    const handleChange = (event) => {
        const { name, value } = event.target;
        setCurrData({ ...currData, [name]: value })
    }

    const handleNumberChange = (name, value) => {
        setCurrData({ ...currData, [name]: value })
    }

    const handleSubmit = (ev) => {
        setLoadingButton('save');
        const data: PatchJsonObj<IEditHouseholdsituationData> = {
            data: {
                type: 'user--user',
                id: allUserData?.id,
                attributes: {
                    ...currData,
                },
            }
        };
    
        return JsonApiRequest.patchRequest<any>(`/user/user/${allUserData?.id}`, data)
            .then((data) => {
                submitPropertyRequest();
            })
            .catch((e) => {
                setLoadingButton(null)
                return Promise.reject(e);
            });
    }

    const handleSubmitSkip = () => {
        setLoadingButton('skip');
        submitPropertyRequest();
    }

    return (
        <div className={styles.propertyMessage}>
            <TextField
                label="Geburtsdatum"
                value={currData?.date_of_birth || dateOfBirth || ''}
                name="date_of_birth"
                inputProps={{
                    min: `1900-01-01`,
                    max: `${new Date().getFullYear()}-12-31`,
                }}
                type="date"
                onChange={handleDateChange}
                onBlur={handleOnBlurDate}
            />
            <TextField
                label="Geburtsort"
                name="place_of_birth"
                value={currData?.place_of_birth || ''}
                onChange={handleChange}
            />
            <Select
                label="Familienstand"
                name="marital_status"
                value={currData?.marital_status || ''}
                onChange={handleChange}
            >
                <MenuItem value="">Keine Auswahl</MenuItem>
                {Object.keys(maritalStatusList).map((key) => {
                    return <MenuItem key={key} value={key}>{maritalStatusList[key]}</MenuItem>
                })
                }
            </Select>
            <Select
                label="Staatsangehörigkeit"
                name="nationality"
                value={currData?.nationality || ''}
                onChange={handleChange}
            >
                <MenuItem value="">Keine Auswahl</MenuItem>
                {Object.keys(nationalityList).map((key) => {
                    return <MenuItem key={key} value={key}>{nationalityList[key]}</MenuItem>
                })
                }
            </Select>
            <Select
                label="Wohnsituation"
                name="housing_situation"
                value={currData?.housing_situation || ''}
                onChange={handleChange}>
                <MenuItem value="">Keine Auswahl</MenuItem>
                {Object.keys(housingSituationList).map((key) => {
                    return <MenuItem key={key} value={key}>{housingSituationList[key]}</MenuItem>
                })
                }
            </Select>
            <Select
                label="Beschäftigungsart"
                name="type_of_employment"
                value={currData?.type_of_employment || ''}
                onChange={handleChange}
            >
                <MenuItem value="">Keine Auswahl</MenuItem>
                {Object.keys(typeOfEmploymentList).map((key) => {
                    return <MenuItem key={key} value={key}>{typeOfEmploymentList[key]}</MenuItem>
                })}
            </Select>
            <NumberField
                label="Haushaltsnettoeinkommen"
                name="net_income"
                value={currData?.net_income || ''}
                onChange={handleNumberChange}
                suffix={currData?.net_income ? ' €' : null}
                // suffix={  ' €'  }
                autocomplete="off"
                allowEmptyFormatting
            />
            <Alert severity="info">
                Bitte beachte, dass diese Daten nach deiner Annahme der anschließenden Beratungsanfragen solange mit deinen Berater:innen geteilt werden, 
                wie ihr miteinander verbunden bleibt. Du kannst die Beziehung zu Berater:innen jederzeit einseitig beenden. 
                Dein/e Beraterin sieht dich dann nicht mehr in der Kundenliste.
            </Alert>
            <div className={styles.saveButtons}>
                <Button type="submit" loading={loadingButton === 'skip'} color="secondary" variant="text" onClick={handleSubmitSkip} disabled={loadingButton !== null}>Überspringen</Button>
                <Button type="submit" loading={loadingButton === 'save'} onClick={handleSubmit} disabled={loadingButton !== null}>Speichern</Button>
            </div>
        </div>
    );
};

export default PropertyMessageDetails;
